/**
 * Single in-flight refresh so parallel 401s / Strict Mode don't POST /refresh twice
 * (rotation would invalidate the first token before the second request).
 */
export const AUTH_TOKEN_KEY = 'auth_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';

/** Fired when refresh fails and the user must sign in again. */
export const AUTH_SESSION_EXPIRED_EVENT = 'auth:session-expired';

/** Refresh this many seconds before JWT exp so server restarts don't strand users on expiry. */
const ACCESS_TOKEN_REFRESH_SKEW_SEC = 60;

export type RefreshFailureReason = 'unauthorized' | 'network';

export type RefreshResult =
  | { ok: true; token: string }
  | { ok: false; reason: RefreshFailureReason };

let inFlight: Promise<RefreshResult> | null = null;

/** Decode JWT exp without verifying (client-side expiry hint only). */
export function isAccessTokenExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  const parts = token.split('.');
  if (parts.length < 2) return true;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now() + ACCESS_TOKEN_REFRESH_SKEW_SEC * 1000;
  } catch {
    return true;
  }
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setStoredRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearStoredRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function clearStoredAuthTokens(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function notifySessionExpired(): void {
  clearStoredAuthTokens();
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
}

function persistRefreshResponse(data: { accessToken?: string; token?: string; refreshToken?: string }): RefreshResult {
  const newToken = data.accessToken ?? data.token;
  if (!newToken) {
    return { ok: false, reason: 'unauthorized' };
  }
  localStorage.setItem(AUTH_TOKEN_KEY, newToken);
  if (typeof data.refreshToken === 'string' && data.refreshToken.length > 0) {
    setStoredRefreshToken(data.refreshToken);
  }
  return { ok: true, token: newToken };
}

async function postRefresh(): Promise<RefreshResult> {
  try {
    const storedRefresh = getStoredRefreshToken();
    // Single request: credentials send HttpOnly cookie when present; body carries
    // localStorage refresh when cookies are blocked (common behind reverse proxies).
    // Server prefers cookie over body, so a stale body cannot override a valid cookie.
    const init: RequestInit = {
      method: 'POST',
      credentials: 'include',
    };
    if (storedRefresh) {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify({ refreshToken: storedRefresh });
    }

    const res = await fetch('/api/auth/refresh', init);

    if (res.status === 401 || res.status === 403) {
      clearStoredRefreshToken();
      return { ok: false, reason: 'unauthorized' };
    }
    if (!res.ok) {
      return { ok: false, reason: 'network' };
    }
    const data = await res.json();
    return persistRefreshResponse(data);
  } catch {
    return { ok: false, reason: 'network' };
  }
}

export async function refreshAccessToken(): Promise<RefreshResult> {
  if (!inFlight) {
    inFlight = postRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Convenience wrapper for callers that only need the token string. */
export async function refreshAccessTokenOrNull(): Promise<string | null> {
  const result = await refreshAccessToken();
  return result.ok ? result.token : null;
}

/**
 * Returns a valid access token, refreshing via HttpOnly cookie or stored refresh token
 * when missing or near expiry.
 */
export async function ensureAccessToken(): Promise<RefreshResult> {
  const stored = getStoredAccessToken();
  if (stored && !isAccessTokenExpired(stored)) {
    return { ok: true, token: stored };
  }
  return refreshAccessToken();
}

/** True when the session cannot be recovered without signing in again. */
export function isSessionDead(): boolean {
  const access = getStoredAccessToken();
  if (access && !isAccessTokenExpired(access)) {
    return false;
  }
  return !getStoredRefreshToken();
}
